// Admin notifications API client (v92).
//
// Three endpoints:
//   GET  /api/admin/notifications        — current settings
//   PUT  /api/admin/notifications        — update settings
//   GET  /api/admin/notifications/queue  — recent queue rows
//   POST /api/admin/notifications/test   — send a test email

import useSWR from 'swr';

export interface NotificationSettings {
  enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  smtp_tls: 'starttls' | 'tls' | 'none';
  from_address: string;
  from_name: string;
  notify_block_created: boolean;
  notify_block_hit: boolean;
}

export interface NotificationSettingsResponse {
  settings: NotificationSettings;
  has_password: boolean;
}

export type QueueStatus = 'pending' | 'sent' | 'failed' | 'skipped';
export type QueueKind = 'block_created' | 'block_hit' | 'test';

export interface QueueItem {
  ID: number;
  Kind: QueueKind;
  RecipientEmail: string;
  Subject: string;
  Body: string;
  BlockID: string | null;
  ArtifactPath: string;
  Status: QueueStatus;
  Attempts: number;
  LastError: string;
  NextAttemptAt: string;
  CreatedAt: string;
  SentAt: string | null;
}

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useNotificationSettings() {
  return useSWR<NotificationSettingsResponse>('/api/admin/notifications', fetcher);
}

export function useNotificationQueue(limit = 50) {
  return useSWR<QueueItem[]>(`/api/admin/notifications/queue?limit=${limit}`, fetcher, {
    refreshInterval: 15_000,
  });
}

export async function saveNotificationSettings(s: NotificationSettings): Promise<void> {
  const res = await fetch('/api/admin/notifications', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(s),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function sendTestEmail(to?: string): Promise<{ status: string; recipient: string }> {
  const res = await fetch('/api/admin/notifications/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ to: to ?? '' }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
